<template>
  <div v-if="leadCard" class="mb-16">
    <section class="grid min-h-[78vh] gap-10 py-6 lg:grid-cols-[minmax(0,0.44fr)_minmax(0,0.56fr)] lg:items-center">
      <div class="max-w-md">
        <p class="text-[0.68rem] uppercase tracking-[0.34em] text-gray-400">{{ copy.heroLabel }}</p>
        <h2
          :class="heroTitleClass"
          :lang="currentLocale"
        >
          {{ heroTitle }}
        </h2>
        <p v-if="leadCard?.colorLabel" class="mt-4 text-[0.72rem] uppercase tracking-[0.28em] text-gray-500">
          {{ leadCard.colorLabel }}
        </p>
        <p v-if="resolvedEditorialHook" class="mt-6 text-[1.1rem] italic tracking-wide text-gray-500 md:text-[1.2rem]">
          {{ resolvedEditorialHook }}
        </p>
        <div class="mt-8 border-t border-gray-200 pt-7">
          <div v-if="displayPrice && !showFoundingAccessPrice" class="max-w-[31rem]">
            <p class="text-[0.6rem] uppercase tracking-[0.34em]" aria-hidden="true">&nbsp;</p>
            <p class="mt-6 font-['Playfair_Display'] text-[2.35rem] leading-none text-black md:text-[2.85rem]">
              {{ displayPrice }} €
            </p>
          </div>
          <ProductFoundingAccessPrice
            v-if="showFoundingAccessPrice"
            class="-mt-7 pt-7"
            :color="leadCard.colorKey"
            :locale-prefix="localePrefix"
            :retail-price="displayPrice"
          />
          <p :class="showFoundingAccessPrice ? 'mt-5' : 'mt-6'" class="text-[0.76rem] leading-5 text-gray-500">
            {{ copy.rarityLineOne }}<template v-if="showFoundingAccessPrice"> {{ copy.deliveryLine }}</template>
          </p>
          <p
            v-if="showPrivateRelaunch && !showFoundingAccessPrice"
            class="mt-5 max-w-sm border-l border-black/15 pl-4 text-[0.82rem] leading-6 text-gray-600"
          >
            {{ copy.privateRelaunchNote }}
          </p>
          <NuxtLink
            v-if="showPrivateRelaunch && !showFoundingAccessPrice"
            :to="privateAccessCtaPath"
            data-private-access-cta="product_collection_hero"
            class="mt-6 inline-flex w-full items-center justify-center rounded-full bg-black px-7 py-4 text-center text-[0.66rem] font-medium uppercase tracking-[0.14em] text-white transition-colors hover:bg-gray-900 sm:w-auto sm:tracking-[0.22em] whitespace-nowrap"
            @click="handlePrivateRelaunchClick('hero', $event)"
          >
            {{ ctaLabel }}
          </NuxtLink>
        </div>
      </div>

      <NuxtLink :to="leadCard.href" class="block">
        <div
          class="relative"
        >
          <KImage
            v-if="leadCard.image"
            :src="leadCard.image"
            :alt="`${productLabel} ${leadCard.colorLabel}`"
            class="relative z-10 w-full"
            sizes="100vw lg:58vw xl:58vw 2xl:58vw"
            loading="eager"
            :quality="92"
          />
          <div
            v-else
            class="flex aspect-[4/5] items-center justify-center px-6 text-center text-[0.65rem] uppercase tracking-[0.22em] text-gray-400"
          >
            No image in feed
          </div>
        </div>
      </NuxtLink>
    </section>

    <div
      v-if="showFoundingAccessPrice"
      class="relative left-1/2 mb-8 w-screen -translate-x-1/2 sm:mb-10"
    >
      <HomeLuxuryPrivateAccess
        compact
        embedded
      />
    </div>

    <section class="flex min-h-[44vh] items-center border-y border-gray-200 py-20">
      <div class="max-w-full">
        <p class="font-serif text-[2.5rem] font-light leading-[1.12] tracking-[0] text-black md:text-[3.2rem] md:leading-[1.08] lg:whitespace-nowrap lg:text-[3.75rem] xl:text-[4.35rem] 2xl:text-[4.7rem]">
          {{ signatureLines.primary }}
        </p>
        <p class="mt-10 font-serif text-[2.5rem] font-light leading-[1.12] tracking-[0] text-black md:mt-12 md:text-[3.2rem] md:leading-[1.08] lg:whitespace-nowrap lg:text-[3.75rem] xl:text-[4.35rem] 2xl:text-[4.7rem]">
          {{ signatureLines.secondary }}
        </p>
      </div>
    </section>

    <section v-if="showPalette" ref="paletteSection" id="palette" class="scroll-mt-24 py-20">
      <div class="max-w-3xl">
        <p class="text-[0.68rem] uppercase tracking-[0.34em] text-gray-400">{{ copy.paletteLabel }}</p>
      </div>

      <div class="mt-12 grid grid-cols-2 gap-x-8 gap-y-16 lg:grid-cols-4 lg:gap-x-12">
        <button
          v-for="card in visibleCards"
          :key="card.id"
          type="button"
          class="signature-palette-item group block w-full text-left outline-none transition-opacity duration-300 focus-visible:ring-1 focus-visible:ring-black/30 focus-visible:ring-offset-4"
          :class="interactivePaletteId && interactivePaletteId !== card.id ? 'lg:opacity-35' : 'opacity-100'"
          :aria-pressed="resolvedFocusCard?.id === card.id ? 'true' : 'false'"
          @mouseenter="hoveredPaletteId = card.id"
          @mouseleave="hoveredPaletteId = null"
          @focus="hoveredPaletteId = card.id"
          @blur="hoveredPaletteId = null"
          @click="selectPaletteCard(card.id, card.colorKey)"
        >
          <div class="relative aspect-[4/3] px-6 py-6">
            <div class="relative z-10 h-full w-full overflow-visible">
              <KImage
                v-if="card.image"
                :src="card.image"
                :alt="`${productLabel} ${card.colorLabel}`"
                class="w-full object-contain object-center transition-transform duration-500 group-hover:-translate-y-1.5 group-hover:scale-[1.035]"
                sizes="100vw md:50vw lg:25vw"
                :quality="86"
              />
              <div
                v-else
                class="flex h-full items-center justify-center px-6 text-center text-[0.65rem] uppercase tracking-[0.22em] text-gray-400"
              >
                No image in feed
              </div>
            </div>
          </div>

          <div class="mt-3 text-center">
            <p class="text-[0.82rem] uppercase tracking-[0.34em] text-gray-500">{{ card.colorLabel }}</p>
          </div>
        </button>
      </div>
    </section>

    <section
      ref="focusSection"
      class="grid gap-12 pt-20 lg:grid-cols-[minmax(0,1.18fr)_minmax(0,0.82fr)] lg:items-start"
      :class="showPalette ? 'border-t border-gray-200' : ''"
    >
      <NuxtLink
        :to="resolvedFocusCard?.href || leadCard.href"
        class="block"
      >
        <div
          class="relative"
        >
          <Transition name="signature-focus-fade" mode="out-in">
            <KImage
              v-if="resolvedFocusCard?.image"
              :key="resolvedFocusCard.id"
              :src="resolvedFocusCard.image"
              :alt="`${productLabel} ${resolvedFocusCard.colorLabel}`"
              class="relative z-10 w-full"
              sizes="100vw lg:54vw xl:54vw 2xl:54vw"
              :quality="92"
            />
            <div
              v-else
              key="focus-placeholder"
              class="flex aspect-[4/5] items-center justify-center px-6 text-center text-[0.65rem] uppercase tracking-[0.22em] text-gray-400"
            >
              No image in feed
            </div>
          </Transition>
        </div>
      </NuxtLink>

      <div class="max-w-[17rem] lg:justify-self-center lg:pt-12">
        <h3
          :class="focusTitleClass"
          :lang="currentLocale"
        >
          {{ heroTitle }}
        </h3>
        <p v-if="resolvedFocusCard?.colorLabel" class="mt-4 text-[0.72rem] uppercase tracking-[0.28em] text-gray-500">
          {{ resolvedFocusCard.colorLabel }}
        </p>

        <div v-if="!showFocusFoundingAccessPrice" class="mt-8 max-w-[31rem] border-t border-black/12 pt-5">
          <!-- Same height as the "Founder price" label so the price sits at the same level in both states. -->
          <p class="text-[0.6rem] uppercase tracking-[0.34em]" aria-hidden="true">&nbsp;</p>
          <p class="mt-6 font-['Playfair_Display'] text-[2.35rem] leading-none text-black md:text-[2.85rem]">
            {{ focusDisplayPrice }} €
          </p>
        </div>
        <ProductFoundingAccessPrice
          v-else
          class="mt-8"
          :color="resolvedFocusCard?.colorKey || leadCard.colorKey"
          :locale-prefix="localePrefix"
          :retail-price="focusDisplayPrice"
        />
        <div :class="showFocusFoundingAccessPrice ? 'mt-10' : 'mt-6'" class="space-y-2 text-[0.82rem] leading-6 text-gray-500 md:text-[0.88rem]">
          <p>{{ copy.rarityLineOne }}</p>
          <p v-if="showFocusFoundingAccessPrice">{{ copy.deliveryLine }}</p>
        </div>
        <p
          v-if="showPrivateRelaunch && !showFocusFoundingAccessPrice"
          class="mt-6 border-l border-black/15 pl-4 text-[0.82rem] leading-6 text-gray-600"
        >
          {{ copy.privateRelaunchNote }}
        </p>

        <NuxtLink
          v-if="showPrivateRelaunch && !showFocusFoundingAccessPrice"
          :to="focusPrivateAccessCtaPath"
          data-private-access-cta="product_collection_focus"
          class="mt-12 inline-flex items-center justify-center rounded-full bg-black px-8 py-4 text-[0.66rem] font-medium uppercase tracking-[0.14em] text-white transition-colors hover:bg-gray-900 sm:tracking-[0.22em] whitespace-nowrap"
          @click="handlePrivateRelaunchClick('focus', $event)"
        >
          {{ focusCtaLabel }}
        </NuxtLink>
      </div>
    </section>

    <section
      v-if="detailPhotos.length"
      class="border-t border-gray-200 py-20 md:py-28"
    >
      <p class="mb-8 text-[0.7rem] uppercase tracking-[0.32em] text-gray-400">{{ copy.detailsLabel }}</p>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
        <figure v-for="(src, index) in detailPhotos" :key="src" class="overflow-hidden bg-[#e4d8c8]">
          <button
            type="button"
            class="group/detail relative block h-full w-full cursor-zoom-in outline-none focus-visible:ring-1 focus-visible:ring-black/40"
            :aria-label="`${heroTitle} — ${copy.detailsLabel} ${index + 1}`"
            @click="openDetailPhoto(index)"
          >
          <KImage
            :src="src"
            :alt="`${heroTitle} — ${copy.detailsLabel} ${index + 1}`"
            width="1000"
            sizes="sm:50vw md:33vw lg:420px"
            :quality="90"
            class="block aspect-[2/3] h-full w-full object-cover"
          />
          <span class="pointer-events-none absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-black opacity-0 shadow-md transition duration-200 group-hover/detail:opacity-100 group-focus-visible/detail:opacity-100">
            <ZoomIn class="h-4 w-4" :stroke-width="1.5" aria-hidden="true" />
          </span>
          </button>
        </figure>
      </div>
      <LookbookModal
        :show="detailPhotoIndex !== null"
        :modal-image="detailPhotoIndex !== null ? detailPhotos[detailPhotoIndex] || '' : ''"
        :alt-text="`${heroTitle} — ${copy.detailsLabel}`"
        :product-type="resolvedLookbookProductType || 'ankle-boots'"
        :is-lookbook-image="false"
        @close="detailPhotoIndex = null"
        @navigate-lookbook="navigateDetailPhoto"
      />
    </section>

    <section
      v-if="resolvedLookbookProductType && resolvedLookbookCard"
      class="border-t border-gray-200 py-24 md:py-32"
    >
      <div class="mb-6 max-w-xl">
        <p class="text-[0.7rem] uppercase tracking-[0.32em] text-gray-400">{{ copy.lookbookLabel }}</p>
        <p class="mt-5 text-[1.25rem] font-light leading-[1.2] tracking-[-0.025em] text-black md:text-[1.65rem]">
          {{ copy.lookbookBridge }}
        </p>
      </div>

      <LookbookGrid
        :key="`${resolvedLookbookProductType}-${resolvedLookbookCard.colorKey}`"
        :product-type="resolvedLookbookProductType"
        :color="resolvedLookbookCard.colorKey"
        :api-photos="resolvedLookbookCard.photos"
        :api-photos-zoom="resolvedLookbookCard.photosZoom"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { ZoomIn } from 'lucide-vue-next'
import { buildProductColorPath, getProductTypeRouteSlug } from '~/utils/colorRoute'
import { getLocaleFromPath, type SiteLocaleCode } from '~/utils/locales'
import { buildPrivateAccessPath } from '~/utils/privateAccessLinks'
import { useSupabaseClient } from '~/composables/useSupabaseClient'

interface FeedVariant {
  id?: string
  option1?: string | null
  option2?: string | null
  price_amount?: number | null
  images?: string[] | null
  photos?: string[] | null
  photos_zoom?: string[] | null
}

interface FeedProduct {
  id?: string
  title?: string
  slug?: string
  price_amount?: number | null
  images?: string[] | null
  variants?: FeedVariant[] | null
}

interface FallbackCard {
  color: string
  title?: string
  image?: string
  price?: number | null
}

function normalizeLookbookMediaKey(path: string) {
  const fileName = path.split('/').pop()?.split('?')[0] || path
  return fileName.replace(/z(?=\.[a-z0-9]+$)/i, '').toLowerCase()
}

function buildLookbookMediaPairs(variants: FeedVariant[]) {
  const pairs = new Map<string, { photo: string, zoom: string }>()

  for (const variant of variants) {
    const photos = Array.isArray(variant.photos) ? variant.photos.filter(Boolean) : []
    const photosZoom = Array.isArray(variant.photos_zoom) ? variant.photos_zoom.filter(Boolean) : []
    const zoomByKey = new Map(
      photosZoom.map(zoomPath => [normalizeLookbookMediaKey(zoomPath), zoomPath] as const)
    )

    for (let index = 0; index < photos.length; index++) {
      const photoPath = photos[index]
      if (!photoPath) continue

      const zoomPath = photosZoom[index] || zoomByKey.get(normalizeLookbookMediaKey(photoPath)) || ''
      const existingPair = pairs.get(photoPath)

      if (existingPair) {
        if (!existingPair.zoom && zoomPath) {
          existingPair.zoom = zoomPath
        }
        continue
      }

      pairs.set(photoPath, {
        photo: photoPath,
        zoom: zoomPath
      })
    }
  }

  return Array.from(pairs.values())
}

const props = withDefaults(defineProps<{
  collectionName?: string
  collectionSlug?: string
  collectionSlogan?: string
  collectionDescription?: string
  headline?: string
  editorialHook?: string
  displayOrder?: string[]
  items: Array<{ products?: FeedProduct | null }>
  heroColor?: string
  localePrefix?: string
  productTypePath?: string
  focusPrice?: number | null
  fallbackCards?: FallbackCard[]
}>(), {
  collectionName: '',
  collectionSlug: '',
  collectionSlogan: '',
  collectionDescription: '',
  headline: '',
  editorialHook: '',
  displayOrder: () => [],
  heroColor: '',
  localePrefix: '',
  productTypePath: '',
  focusPrice: null,
  fallbackCards: () => []
})

function resolveFeedProduct(item: { products?: FeedProduct | null; product?: FeedProduct | null; [key: string]: any } | null | undefined) {
  if (!item) return null
  if (item.products) return item.products
  if (item.product) return item.product
  if (Array.isArray(item.variants) || item.slug || item.title || item.id) {
    return item as FeedProduct
  }
  return null
}

const currentLocale = computed<SiteLocaleCode>(() => getLocaleFromPath(`${props.localePrefix || ''}/`))
const route = useRoute()
const signalsKit = import.meta.client ? useSignalsKit() : null
const productLabel = computed(() => props.headline || props.items[0]?.products?.title || props.collectionName || 'Collection')
const resolvedEditorialHook = computed(() => {
  const localHook = props.editorialHook?.trim()
  const contentHook = props.collectionSlogan?.trim()

  if (currentLocale.value === 'en') return contentHook || localHook
  return localHook || contentHook
})

type SignatureCollectionCopy = {
  deliveryLine: string
  focusCta: string
  heroLabel: string
  lookbookBridge: string
  detailsLabel: string
  lookbookLabel: string
  paletteLabel: string
  privateRelaunchNote: string
  rarityLineOne: string
}

const COPY_BY_LOCALE = {
  en: {
    deliveryLine: 'Estimated delivery: 8-10 weeks.',
    focusCta: 'Join the private relaunch',
    heroLabel: 'The statement',
    lookbookBridge: 'Defined in motion. Refined in form.',
    detailsLabel: 'The details',
    lookbookLabel: 'In motion',
    paletteLabel: 'Palette',
    privateRelaunchNote: 'This pair is not in open sale right now. A small private relaunch is being prepared.',
    rarityLineOne: 'Produced in limited quantities.'
  },
  fr: {
    deliveryLine: 'Livraison estimée : 8 à 10 semaines.',
    focusCta: 'Rejoindre la relance privée',
    heroLabel: 'Le parti pris',
    lookbookBridge: 'Définie en mouvement. Affinée dans la forme.',
    detailsLabel: 'Les détails',
    lookbookLabel: 'En mouvement',
    paletteLabel: 'Palette',
    privateRelaunchNote: 'Cette paire n’est pas en vente libre pour l’instant. Une petite relance privée se prépare.',
    rarityLineOne: 'Produite en quantités limitées.'
  },
  es: {
    deliveryLine: 'Entrega estimada: 8 a 10 semanas.',
    focusCta: 'Solicitar acceso',
    heroLabel: 'La postura',
    lookbookBridge: 'Definida en movimiento. Afinada en la forma.',
    detailsLabel: 'Los detalles',
    lookbookLabel: 'En movimiento',
    paletteLabel: 'Paleta',
    privateRelaunchNote: 'Esta pieza no está en venta abierta por ahora. Se prepara un pequeño relanzamiento privado.',
    rarityLineOne: 'Producida en cantidades limitadas.'
  },
  pt: {
    deliveryLine: 'Entrega estimada: 8 a 10 semanas.',
    focusCta: 'Pedir acesso',
    heroLabel: 'A postura',
    lookbookBridge: 'Definida em movimento. Afinada na forma.',
    detailsLabel: 'Os detalhes',
    lookbookLabel: 'Em movimento',
    paletteLabel: 'Paleta',
    privateRelaunchNote: 'Esta peça não está em venda aberta por enquanto. Uma pequena reedição privada está sendo preparada.',
    rarityLineOne: 'Produzida em quantidades limitadas.'
  },
  ja: {
    deliveryLine: 'お届け予定：8〜10週間。',
    focusCta: 'アクセスを申請',
    heroLabel: 'ステートメント',
    lookbookBridge: '動きの中で定義され、形の中で磨かれます。',
    detailsLabel: 'ディテール',
    lookbookLabel: '動きの中で',
    paletteLabel: 'パレット',
    privateRelaunchNote: '現在は一般販売を行っていません。小さなプライベート再始動を準備しています。',
    rarityLineOne: '数量限定で製作。'
  },
  ko: {
    deliveryLine: '예상 배송: 8-10주.',
    focusCta: '액세스 요청',
    heroLabel: '스테이트먼트',
    lookbookBridge: '움직임 속에서 정의되고, 형태 안에서 다듬어집니다.',
    detailsLabel: '디테일',
    lookbookLabel: '움직임',
    paletteLabel: '팔레트',
    privateRelaunchNote: '현재 일반 판매 중이 아닙니다. 작은 비공개 재출시를 준비하고 있습니다.',
    rarityLineOne: '한정 수량으로 제작됩니다.'
  },
  de: {
    deliveryLine: 'Voraussichtliche Lieferung: 8 bis 10 Wochen.',
    focusCta: 'Zugang anfragen',
    heroLabel: 'Die Haltung',
    lookbookBridge: 'In Bewegung definiert. In der Form verfeinert.',
    detailsLabel: 'Die Details',
    lookbookLabel: 'In Bewegung',
    paletteLabel: 'Palette',
    privateRelaunchNote: 'Dieses Paar ist derzeit nicht im freien Verkauf. Ein kleiner privater Relaunch wird vorbereitet.',
    rarityLineOne: 'In limitierter Stückzahl gefertigt.'
  },
  it: {
    deliveryLine: 'Consegna stimata: 8-10 settimane.',
    focusCta: 'Richiedere accesso',
    heroLabel: 'La postura',
    lookbookBridge: 'Definita in movimento. Rifinita nella forma.',
    detailsLabel: 'I dettagli',
    lookbookLabel: 'In movimento',
    paletteLabel: 'Palette',
    privateRelaunchNote: 'Questo paio non è in vendita libera al momento. È in preparazione un piccolo rilancio privato.',
    rarityLineOne: 'Prodotta in quantità limitata.'
  },
  zh: {
    deliveryLine: '预计交付：8-10周。',
    focusCta: '申请访问',
    heroLabel: '姿态',
    lookbookBridge: '在动态中定义，在形态中精修。',
    detailsLabel: '细节',
    lookbookLabel: '动态中',
    paletteLabel: '色彩',
    privateRelaunchNote: '这双鞋目前不公开销售。一次小规模私密重启正在准备中。',
    rarityLineOne: '限量制作。'
  }
} satisfies Record<SiteLocaleCode, SignatureCollectionCopy>

const copy = computed(() => COPY_BY_LOCALE[currentLocale.value] || COPY_BY_LOCALE.en)

const activePreorderCampaigns = ref(new Set<string>())

const signatureLinesMap = {
  axis: {
    en: {
      primary: 'A structure that holds the body.',
      secondary: 'A line that defines the stance.'
    },
    fr: {
      primary: 'Une structure qui tient le corps.',
      secondary: 'Une ligne qui définit l’allure.'
    },
    es: {
      primary: 'Una estructura que sostiene el cuerpo.',
      secondary: 'Una línea que define la postura.'
    },
    pt: {
      primary: 'Uma estrutura que sustenta o corpo.',
      secondary: 'Uma linha que define a postura.'
    },
    ja: {
      primary: '身体を支える構造。',
      secondary: '姿勢を定めるライン。'
    },
    ko: {
      primary: '몸을 지탱하는 구조.',
      secondary: '자세를 정의하는 라인.'
    },
    de: {
      primary: 'Eine Struktur, die den Körper hält.',
      secondary: 'Eine Linie, die die Haltung definiert.'
    },
    it: {
      primary: 'Una struttura che sostiene il corpo.',
      secondary: 'Una linea che definisce la postura.'
    },
    zh: {
      primary: '支撑身体的结构。',
      secondary: '定义姿态的线条。'
    }
  },
  align: {
    en: {
      primary: 'Alignment starts before the step.',
      secondary: 'A line that settles the posture.'
    },
    fr: {
      primary: 'L’alignement commence avant le pas.',
      secondary: 'Une ligne qui installe la posture.'
    },
    es: {
      primary: 'La alineación empieza antes del paso.',
      secondary: 'Una línea que instala la postura.'
    },
    pt: {
      primary: 'O alinhamento começa antes do passo.',
      secondary: 'Uma linha que instala a postura.'
    },
    ja: {
      primary: '一歩の前に、アラインメントは始まります。',
      secondary: '姿勢を整えるライン。'
    },
    ko: {
      primary: '걸음 이전에 정렬이 시작됩니다.',
      secondary: '자세를 잡아주는 라인.'
    },
    de: {
      primary: 'Ausrichtung beginnt vor dem Schritt.',
      secondary: 'Eine Linie, die Haltung setzt.'
    },
    it: {
      primary: 'L’allineamento inizia prima del passo.',
      secondary: 'Una linea che stabilisce la postura.'
    },
    zh: {
      primary: '迈步之前，平衡已经开始。',
      secondary: '安定姿态的线条。'
    }
  },
  edge: {
    en: {
      primary: 'A sharper line.',
      secondary: 'A stance that arrives before movement.'
    },
    fr: {
      primary: 'Une ligne plus tranchante.',
      secondary: 'Une allure qui arrive avant le mouvement.'
    },
    es: {
      primary: 'Una línea más precisa.',
      secondary: 'Una presencia que llega antes del movimiento.'
    },
    pt: {
      primary: 'Uma linha mais precisa.',
      secondary: 'Uma presença que chega antes do movimento.'
    },
    ja: {
      primary: 'よりシャープなライン。',
      secondary: '動きの前に届く存在感。'
    },
    ko: {
      primary: '더 선명한 라인.',
      secondary: '움직임보다 먼저 도착하는 존재감.'
    },
    de: {
      primary: 'Eine schärfere Linie.',
      secondary: 'Eine Haltung, die vor der Bewegung ankommt.'
    },
    it: {
      primary: 'Una linea più netta.',
      secondary: 'Una presenza che arriva prima del movimento.'
    },
    zh: {
      primary: '更锋利的线条。',
      secondary: '先于动作抵达的姿态。'
    }
  },
  poise: {
    en: {
      primary: 'An open line that keeps the body held.',
      secondary: 'A stance made clear without raising the voice.'
    },
    fr: {
      primary: 'Une ligne ouverte qui tient le corps.',
      secondary: 'Une allure nette, sans élever la voix.'
    },
    es: {
      primary: 'Una línea abierta que sostiene el cuerpo.',
      secondary: 'Una postura clara sin elevar la voz.'
    },
    pt: {
      primary: 'Uma linha aberta que sustenta o corpo.',
      secondary: 'Uma postura clara sem elevar a voz.'
    },
    ja: {
      primary: '身体を支えるオープンなライン。',
      secondary: '声を上げずに伝わる明確な姿勢。'
    },
    ko: {
      primary: '몸을 지탱하는 열린 라인.',
      secondary: '목소리를 높이지 않아도 분명한 자세.'
    },
    de: {
      primary: 'Eine offene Linie, die den Körper hält.',
      secondary: 'Eine klare Haltung, ohne laut zu werden.'
    },
    it: {
      primary: 'Una linea aperta che sostiene il corpo.',
      secondary: 'Una postura chiara senza alzare la voce.'
    },
    zh: {
      primary: '支撑身体的开放线条。',
      secondary: '无需提高声量，也清晰有力的姿态。'
    }
  }
} as const

const colorPriority: Record<string, number> = {
  black: 0,
  red: 1,
  carmine: 1,
  beige: 2,
  ivoire: 2,
  ivory: 2,
  yellow: 3,
  saffran: 3,
  saffron: 3,
  safran: 3
}

const colorSlugMap: Record<string, string> = {
  black: 'black',
  saffran: 'yellow',
  saffron: 'yellow',
  safran: 'yellow',
  yellow: 'yellow',
  carmine: 'red',
  red: 'red',
  ivoire: 'beige',
  ivory: 'beige',
  beige: 'beige'
}

const productSlugPathMap: Record<string, string> = {
  'axis-110-boot': '/boots',
  'align-110-ankle-boot': '/ankle-boots',
  'edge-110-pump': '/pumps',
  'poise-110-sandal': '/sandals'
}

const collectionSlugPathMap: Record<string, string> = {
  axis: '/boots',
  align: '/ankle-boots',
  edge: '/pumps',
  poise: '/sandals'
}

const lookbookProductTypes = ['pumps', 'sandals', 'ankle-boots', 'boots'] as const
type LookbookProductType = typeof lookbookProductTypes[number]

function isLookbookProductType(value: string): value is LookbookProductType {
  return (lookbookProductTypes as readonly string[]).includes(value)
}

function resolveProductPath(productSlug?: string) {
  const collectionSlug = props.collectionSlug?.trim().toLowerCase()
  if (collectionSlugPathMap[collectionSlug]) return collectionSlugPathMap[collectionSlug]

  const slug = productSlug?.trim().toLowerCase()
  if (!slug) return props.productTypePath || ''
  if (productSlugPathMap[slug]) return productSlugPathMap[slug]
  if (slug.includes('ankle-boot')) return '/ankle-boots'
  if (slug.includes('sandal')) return '/sandals'
  if (slug.includes('pump')) return '/pumps'
  if (slug.includes('boot')) return '/boots'
  return props.productTypePath || ''
}

function resolveCollectionViewPath(productSlug?: string, colorKey?: string) {
  const basePath = props.productTypePath || resolveProductPath(productSlug)
  if (!colorKey) return `${props.localePrefix}/${getProductTypeRouteSlug(basePath.replace(/^\//, ''), props.localePrefix)}`
  return buildProductColorPath(basePath, colorKey, props.localePrefix)
}

const normalizedDisplayOrder = computed(() =>
  props.displayOrder
    .map(color => colorSlugMap[color.trim().toLowerCase()] || color.trim().toLowerCase())
    .filter(Boolean)
)

const localizedColorLabels = computed(() => {
  const labels = {
    en: { beige: 'Ivory', black: 'Black', red: 'Carmine', yellow: 'Saffron' },
    fr: { beige: 'Ivoire', black: 'Noir', red: 'Carmin', yellow: 'Safran' },
    es: { beige: 'Marfil', black: 'Negro', red: 'Carmín', yellow: 'Azafrán' },
    pt: { beige: 'Marfim', black: 'Preto', red: 'Carmim', yellow: 'Açafrão' },
    ja: { beige: 'アイボリー', black: 'ブラック', red: 'カーマイン', yellow: 'サフラン' },
    ko: { beige: '아이보리', black: '블랙', red: '카민', yellow: '사프란' },
    de: { beige: 'Elfenbein', black: 'Schwarz', red: 'Karmin', yellow: 'Safran' },
    it: { beige: 'Avorio', black: 'Nero', red: 'Carminio', yellow: 'Zafferano' },
    zh: { beige: '象牙色', black: '黑色', red: '胭脂红', yellow: '藏红花色' }
  } satisfies Record<SiteLocaleCode, Record<string, string>>

  return labels[currentLocale.value] || labels.en
})

function getLocalizedColorLabel(colorKey: string) {
  return localizedColorLabels.value[colorKey as keyof typeof localizedColorLabels.value] ||
    `${colorKey.slice(0, 1).toUpperCase()}${colorKey.slice(1)}`
}

const fallbackCardsMap = computed(() => {
  const cards = new Map<string, FallbackCard>()

  for (const fallbackCard of props.fallbackCards) {
    const rawColor = fallbackCard.color?.trim().toLowerCase()
    if (!rawColor) continue

    const colorKey = colorSlugMap[rawColor] || rawColor
    cards.set(colorKey, fallbackCard)
  }

  return cards
})

const cards = computed(() => {
  const collectionCards = props.items.flatMap((item) => {
    const product = resolveFeedProduct(item)
    if (!product?.id) return []

    const variants = Array.isArray(product.variants) ? product.variants : []
    const grouped = new Map<string, { colorLabel: string, variants: FeedVariant[] }>()

    for (const variant of variants) {
      const rawColor = variant.option1?.trim()
      if (!rawColor) continue

      const normalizedColor = rawColor.toLowerCase()
      const colorSlug = colorSlugMap[normalizedColor]
      if (!colorSlug) continue

      const existing = grouped.get(colorSlug)
      if (existing) {
        existing.variants.push(variant)
      } else {
        grouped.set(colorSlug, {
          colorLabel: getLocalizedColorLabel(colorSlug),
          variants: [variant]
        })
      }
    }

    return Array.from(grouped.entries()).map(([colorSlug, group]) => {
      const preferredVariant = group.variants.find(variant => Array.isArray(variant.images) && variant.images.length) || group.variants[0]
      const lookbookPairs = buildLookbookMediaPairs(group.variants)
      const photos = lookbookPairs.map(({ photo }) => photo)
      const photosZoom = lookbookPairs.map(({ zoom }) => zoom)

      return {
        id: `${product.id}-${colorSlug}`,
        title: product.title || props.collectionName || 'Collection product',
        colorKey: colorSlug,
        colorLabel: group.colorLabel,
        href: resolveCollectionViewPath(product.slug, colorSlug),
        image: (Array.isArray(preferredVariant?.images) && preferredVariant.images[0]) || (Array.isArray(product.images) && product.images[0]) || '',
        price: preferredVariant?.price_amount || product.price_amount || 0,
        photos,
        photosZoom
      }
    })
  })

  const mergedCards = collectionCards.map((card) => {
    const fallbackCard = fallbackCardsMap.value.get(card.colorKey)

    return {
      ...card,
      title: card.title || fallbackCard?.title || props.collectionName || 'Collection product',
      image: card.image || fallbackCard?.image || '',
      price: card.price || fallbackCard?.price || 0,
      colorLabel: card.colorLabel || getLocalizedColorLabel(card.colorKey)
    }
  })

  for (const [colorKey, fallbackCard] of fallbackCardsMap.value.entries()) {
    if (mergedCards.some(card => card.colorKey === colorKey)) continue

    mergedCards.push({
      id: `fallback-${colorKey}`,
      title: fallbackCard.title || props.collectionName || 'Collection product',
      colorKey,
      colorLabel: getLocalizedColorLabel(colorKey),
      href: resolveCollectionViewPath(props.items[0]?.products?.slug, colorKey),
      image: fallbackCard.image || '',
      price: fallbackCard.price || 0,
      photos: [],
      photosZoom: []
    })
  }

  const displayFilteredCards = normalizedDisplayOrder.value.length
    ? mergedCards.filter(card => normalizedDisplayOrder.value.includes(card.colorKey))
    : mergedCards

  return displayFilteredCards.sort((a, b) => {
    const leftDisplayIndex = normalizedDisplayOrder.value.indexOf(a.colorKey)
    const rightDisplayIndex = normalizedDisplayOrder.value.indexOf(b.colorKey)

    if (leftDisplayIndex !== -1 || rightDisplayIndex !== -1) {
      if (leftDisplayIndex === -1) return 1
      if (rightDisplayIndex === -1) return -1
      return leftDisplayIndex - rightDisplayIndex
    }

    const left = colorPriority[a.colorKey] ?? 99
    const right = colorPriority[b.colorKey] ?? 99
    return left - right
  })
})

const visibleCards = computed(() => cards.value.slice(0, 4))
const showPalette = computed(() => visibleCards.value.length > 1)

const leadCard = computed(() => {
  const preferredHero = props.heroColor.trim().toLowerCase()
  if (preferredHero) {
    const normalizedHero = colorSlugMap[preferredHero] || preferredHero
    return visibleCards.value.find(card => card.colorKey === normalizedHero) || visibleCards.value[0] || null
  }

  return visibleCards.value[0] || null
})

const productTitleSuffixes = [
  'Ankle Boots',
  'Ankle Boot',
  'Stiefeletten',
  'Stiefelette',
  'Botines',
  'Botín',
  'Botas',
  'Bota',
  'Botins',
  'Botim',
  'Bota de Cano Curto',
  'Botas de Cano Curto',
  'Tronchetti',
  'Tronchetto',
  'Stivaletti',
  'Stivaletto',
  'Stivali',
  'Stivale',
  'Stiefel',
  'Zapatos de Salón',
  'Zapato de Salón',
  'Sapatos de Salto',
  'Sapato de Salto',
  'Décolleté',
  'Decolleté',
  'Sandale',
  'Sandales',
  'Sandalia',
  'Sandalias',
  'Sandália',
  'Sandálias',
  'Sandalo',
  'Sandali',
  'Sandal',
  'Sandals',
  'Escarpin',
  'Escarpins',
  'Pumps',
  'Pump',
  'Bottine',
  'Bottines',
  'Botte',
  'Bottes',
  'Boot',
  'Boots',
  'アンクルブーツ',
  'ブーツ',
  'パンプス',
  'サンダル',
  '앵클부츠',
  '부츠',
  '펌프스',
  '샌들',
  '踝靴',
  '短靴',
  '長靴',
  '长靴',
  '靴子',
  '高跟鞋',
  '涼鞋',
  '凉鞋'
]

function stripProductTitleSuffix(title: string) {
  const normalizedTitle = title.trim()
  const sortedSuffixes = [...productTitleSuffixes].sort((a, b) => b.length - a.length)

  for (const suffix of sortedSuffixes) {
    const normalizedSuffix = suffix.toLocaleLowerCase()
    const normalizedLowerTitle = normalizedTitle.toLocaleLowerCase()

    if (normalizedLowerTitle.startsWith(normalizedSuffix)) {
      const strippedTitle = normalizedTitle
        .slice(suffix.length)
        .replace(/^[\s\-–—:]+/, '')
        .trim()
      if (strippedTitle) return strippedTitle
    }

    if (normalizedLowerTitle.endsWith(normalizedSuffix)) {
      const strippedTitle = normalizedTitle
        .slice(0, normalizedTitle.length - suffix.length)
        .replace(/[\s\-–—:]+$/, '')
        .trim()
      if (strippedTitle) return strippedTitle
    }
  }

  return normalizedTitle
}

const heroTitle = computed(() => stripProductTitleSuffix(productLabel.value) || productLabel.value)
const isCjkLocale = computed(() => ['ja', 'ko', 'zh'].includes(currentLocale.value))
const heroTitleClass = computed(() => (
  isCjkLocale.value
    ? 'mt-6 whitespace-normal text-[3.15rem] font-light leading-[0.96] tracking-[-0.025em] text-black [word-break:keep-all] md:text-[5.5rem] md:leading-[0.92]'
    : 'mt-6 text-[3.8rem] font-light leading-[0.9] tracking-[-0.02em] text-black md:text-[6rem]'
))
const focusTitleClass = computed(() => (
  isCjkLocale.value
    ? 'whitespace-normal text-[2.75rem] font-light leading-[0.98] tracking-[-0.025em] text-black [word-break:keep-all] md:text-[3.9rem] md:leading-[0.94]'
    : 'text-[3.1rem] font-light leading-[0.92] tracking-[-0.02em] text-black md:text-[4.25rem]'
))

const signatureLines = computed(() => {
  const slug = props.collectionSlug.trim().toLowerCase() as keyof typeof signatureLinesMap
  const localeKey = currentLocale.value
  const signature = signatureLinesMap[slug]?.[localeKey]

  if (signature) return signature

  return signatureLinesMap.axis[localeKey] || signatureLinesMap.axis.en
})

const displayPrice = computed(() => props.focusPrice || leadCard.value?.price || 0)
const focusDisplayPrice = computed(() => resolvedFocusCard.value?.price || props.focusPrice || leadCard.value?.price || 0)
const currentProductType = computed(() => {
  const rawPath = props.productTypePath || resolveProductPath(props.items[0]?.products?.slug)
  return rawPath.replace(/^\/+/, '')
})
const activeCampaignKey = computed(() => `${currentProductType.value}:${leadCard.value?.colorKey || ''}`)
const showFoundingAccessPrice = computed(() => activePreorderCampaigns.value.has(activeCampaignKey.value))
const showPrivateRelaunch = computed(() => showFoundingAccessPrice.value)
const paletteSection = ref<HTMLElement | null>(null)
const focusSection = ref<HTMLElement | null>(null)
const selectedPaletteId = ref<string | null>(null)
const hoveredPaletteId = ref<string | null>(null)
const interactivePaletteId = computed(() => hoveredPaletteId.value || selectedPaletteId.value)
// Hover only dims the other swatches; swapping the detail block on hover made the layout jump.
const resolvedFocusCard = computed(() => {
  const targetId = selectedPaletteId.value
  if (!targetId) return leadCard.value
  return visibleCards.value.find(card => card.id === targetId) || leadCard.value
})
const focusActiveCampaignKey = computed(() => `${currentProductType.value}:${resolvedFocusCard.value?.colorKey || ''}`)
const showFocusFoundingAccessPrice = computed(() => activePreorderCampaigns.value.has(focusActiveCampaignKey.value))
const resolvedLookbookCard = computed(() => {
  const targetId = selectedPaletteId.value
  if (!targetId) return leadCard.value
  return visibleCards.value.find(card => card.id === targetId) || leadCard.value
})
const ctaLabel = computed(() => {
  return copy.value.focusCta
})
const focusCtaLabel = computed(() => {
  if (!showFocusFoundingAccessPrice.value) return ctaLabel.value
  return currentLocale.value === 'fr' ? 'Ouvrir la précommande' : 'Open preorder'
})
const privateAccessPath = computed(() =>
  buildPrivateAccessPath(route.path, 'private_relaunch_from_product', 'product_page')
)
const privateAccessCtaPath = computed(() => showFoundingAccessPrice.value ? '#private-access' : privateAccessPath.value)
const focusPrivateAccessCtaPath = computed(() => showFocusFoundingAccessPrice.value ? '#private-access' : privateAccessPath.value)

async function refreshActivePreorderCampaigns() {
  if (!import.meta.client) return

  try {
    const supabase = useSupabaseClient()
    const { data, error } = await supabase
      .from('preorder_campaigns')
      .select('product_type, color, start_date, end_date')
      .eq('status', 'active')

    if (error) return

    const now = Date.now()
    const active = new Set<string>()
    for (const campaign of data || []) {
      const started = !campaign.start_date || new Date(campaign.start_date).getTime() <= now
      const notEnded = !campaign.end_date || new Date(campaign.end_date).getTime() > now
      if (!started || !notEnded) continue
      active.add(`${campaign.product_type}:${campaign.color}`)
    }
    activePreorderCampaigns.value = active
  } catch {
    activePreorderCampaigns.value = new Set()
  }
}

// Close-up photos shown on the home page, reused on the product page (all portrait, same frame).
const DETAIL_PHOTOS: Record<string, string[]> = {
  'ankle-boots': [
    '/photo/ankle-boot-black/8.jpg',
    '/photo/ankle-boot-black/2.jpg',
    '/photo/ankle-boot-black/4.jpg',
    '/photo/ankle-boot-black/6.jpg',
    '/photo/ankle-boot-black/1.jpg',
    '/photo/ankle-boot-black/kendrick-ankle-boot-black-detail-4.jpg'
  ]
}
const detailPhotoIndex = ref<number | null>(null)
const openDetailPhoto = (index: number) => { detailPhotoIndex.value = index }
const navigateDetailPhoto = (direction: 'prev' | 'next') => {
  const count = detailPhotos.value.length
  if (!count || detailPhotoIndex.value === null) return
  detailPhotoIndex.value = (detailPhotoIndex.value + (direction === 'next' ? 1 : -1) + count) % count
}
const detailPhotos = computed<string[]>(() => DETAIL_PHOTOS[resolvedLookbookProductType.value || ''] || [])
const resolvedLookbookProductType = computed<LookbookProductType | undefined>(() => {
  const rawPath = props.productTypePath || resolveProductPath(props.items[0]?.products?.slug)
  const productType = rawPath.replace(/^\//, '')
  return isLookbookProductType(productType) ? productType : undefined
})
const relaunchProductType = computed(() => {
  const rawPath = props.productTypePath || resolveProductPath(props.items[0]?.products?.slug)
  return rawPath.replace(/^\//, '') || 'collection'
})
const relaunchColor = computed(() => resolvedLookbookCard.value?.colorKey || leadCard.value?.colorKey || '')
const relaunchColorLabel = computed(() => resolvedLookbookCard.value?.colorLabel || leadCard.value?.colorLabel || '')
const relaunchTrackingPayload = computed(() => ({
  cta_context: 'product_collection',
  destination: privateAccessPath.value,
  intent_context: 'private_relaunch_from_product',
  interaction_type: 'private_relaunch_cta',
  lead_source: 'product_page',
  product_color: relaunchColor.value || undefined,
  product_color_label: relaunchColorLabel.value || undefined,
  product_name: heroTitle.value,
  product_type: relaunchProductType.value,
  collection_slug: props.collectionSlug || undefined,
  source_page_path: route.path,
  source_page_full_path: route.fullPath
}))

function trackPrivateRelaunchClick(ctaLocation: 'hero' | 'focus') {
  if (ctaLocation === 'focus' ? !showFocusFoundingAccessPrice.value : !showPrivateRelaunch.value) return

  signalsKit?.trackEditorialSignal('private_access_cta_click', {
    ...relaunchTrackingPayload.value,
    cta_location: ctaLocation
  })
}

function focusPrivateAccessForm() {
  if (!import.meta.client) return

  const field = document.getElementById('private-access-first-name') as HTMLInputElement | null
  const section = document.getElementById('private-access')

  if (section) {
    const header = document.querySelector('.hd') as HTMLElement | null
    const headerOffset = header && !header.classList.contains('hd--hidden')
      ? Math.round(header.getBoundingClientRect().height + 40)
      : 112
    const top = section.getBoundingClientRect().top + window.scrollY - headerOffset
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  } else {
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  if (field) {
    window.setTimeout(() => {
      field.focus({ preventScroll: true })
    }, 420)
  }

  if (window.location.hash !== '#private-access') {
    history.replaceState(null, '', '#private-access')
  }
}

function handlePrivateRelaunchClick(ctaLocation: 'hero' | 'focus', event: MouseEvent) {
  trackPrivateRelaunchClick(ctaLocation)
  const shouldFocusForm = ctaLocation === 'focus'
    ? showFocusFoundingAccessPrice.value
    : showFoundingAccessPrice.value
  if (!shouldFocusForm) return

  event.preventDefault()
  focusPrivateAccessForm()
}

// Keep the palette in view but never cut the selected product: the whole detail block must fit when it can.
function scrollToPaletteAndProduct(reduceMotion: boolean) {
  const palette = paletteSection.value
  const focus = focusSection.value
  if (!palette || !focus) return

  const headerGap = 96
  const bottomGap = 24
  const paletteTop = palette.getBoundingClientRect().top + window.scrollY
  const focusRect = focus.getBoundingClientRect()
  const focusTop = focusRect.top + window.scrollY
  const focusBottom = focusRect.bottom + window.scrollY

  const showWholeProduct = focusBottom - window.innerHeight + bottomGap
  const keepPaletteTop = paletteTop - headerGap
  const top = showWholeProduct <= keepPaletteTop
    ? keepPaletteTop
    : Math.min(showWholeProduct, focusTop - headerGap)

  window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' })
}

function selectPaletteCard(cardId: string, colorKey: string) {
  selectedPaletteId.value = cardId
  if (process.client) {
    sessionStorage.setItem('selectedProductColor', colorKey)
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    scrollToPaletteAndProduct(reduceMotion)
  }
}

onMounted(() => {
  void refreshActivePreorderCampaigns()
})

watch(showPrivateRelaunch, (visible) => {
  if (!visible) return

  signalsKit?.trackEditorialSignal('private_access_cta_view', {
    ...relaunchTrackingPayload.value,
    cta_location: 'product_collection'
  })
}, { immediate: true })
</script>

<style scoped>
.signature-focus-fade-enter-active,
.signature-focus-fade-leave-active {
  transition: opacity 220ms ease;
}

.signature-focus-fade-enter-from,
.signature-focus-fade-leave-to {
  opacity: 0;
}
</style>
